import { Col, Input, FormGroup, Row, Label } from 'reactstrap';
import { trim } from 'lodash';
import React, {useMemo} from 'react';
import Select from 'react-select';
import parseHtml from 'html-react-parser';

import Rating from '@mui/material/Rating';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

export const InputListSurveyType = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Textarea" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
  { value: "tel", label: "Telephone" },
  { value: "datetime-local", label: "Date and Time" },
  { value: "select", label: "Select" },
  { value: "multi-select", label: "Multi Select" },
  { value: "checkbox", label: "Checkbox" },
  { value: "radio", label: "Radio" },
  { value: "rating", label: "Rating" },
  { value: "hidden", label: "Hidden Field" },
  { value: "category-break", label: "New Section" },
  { value: "form-break", label: "Form Break" }
];

export const searchListSurveyType = (search) => {
  return InputListSurveyType.filter((item) => {
    return item.label.toLowerCase().includes(search?.toLowerCase()) ||
           item.value.toLowerCase().includes(search?.toLowerCase());
    }
    );
};

export const InputExcludeLabels = ["checkbox", "radio", 'hidden', 'category-break', "rating"];
export const InputNextLineLabels = ["textarea"];
export const NonInputLabels = ["category-break", "form-break"];
export const NoValueLabels = ["hidden", ...NonInputLabels];

export function htmlEntityEncode(str) {
  if(!str || typeof str !== 'string') return str;
  if((str?.length ?? 0) <= 0) return str;
  return Array.from(str).map(ch => {
    const cp = ch.codePointAt(0);
    return cp > 127 ? `&#${cp};` : ch;
  }).join('');
}

export const surveyType = {
  centre: 'Customer Survey',
  caregiver: 'Caregiver Survey',
};

export const statusColors = {
  'not_available': 'light',
  'pending': 'primary',
  'copy_link': 'secondary',
  'survey_sent': 'secondary',
  'completed': 'success',
  'expired': 'danger',
};

export const rawStatusHashMap = {
  'not_available': '-',
  'copy_link': 'Link Copied',
  'survey_sent': 'Survey Sent',
  'completed': 'Completed',
  'pending': 'Not Completed',
  'expired': 'Expired',
}

export const InstanceStatus = {
  'not_available': 'not_available',
  'pending': 'pending',
  'link_copied': 'copy_link',
  'survey_sent': 'survey_sent',
  'completed': 'completed',
  'expired': 'expired',
};

export const careInputFormGroup0 = ({
  display,
  name,
  type = "text",
  disabled,
  step,
  defaultValue,
  onEditFunc,
  value,
  hints = "{}",
  required = false,
  ...props
}) => {
  var label = display.includes('<>') ? decodeURI(parseHtml(display)) : trim(display);

  let parsedHints = {};
  try {
    parsedHints = typeof hints === "string" ? JSON.parse(hints) : hints;
  } catch (e) {
    parsedHints = {};
  }

  const { options = [] } = parsedHints;

  const renderInput = () => {
    switch (type) {
      case "select":
        return (
          <Select
            name={name}
            isDisabled={disabled}
            defaultValue={options.find(o => o.value === defaultValue)}
            value={options.find(o => o.value === value)}
            onChange={(selected) =>{
              if(!selected) return;
              onEditFunc({ target: { name, value: selected?.value ?? "" } })
            }}
            {...parsedHints}
          />
        );
      case "multi-select":
        console.log(options);
        return (
          <Select
            name={name}
            isMulti
            isDisabled={disabled}
            defaultValue={options.filter(o => {
              try {
                const parsed = JSON.parse(defaultValue || "[]");
                return parsed.includes(o.value);
              } catch {
                return false;
              }
            })}
            value={options.filter(o => {
              try {
                const parsed = JSON.parse(value || "[]");
                return parsed.includes(o.value);
              } catch {
                return false;
              }
            })}
            onChange={(selected) => {
              if(!selected) return;
              onEditFunc({target: {name, value: JSON.stringify(selected.map(s => s.value))}})
            }}
            {...parsedHints}
          />
        );
      case "checkbox":
        return (
          <FormControlLabel
            control={
              <Checkbox
                name={name}
                checked={value === "true"}
                onChange={(e) => {
                  if(disabled) return;
                  onEditFunc({ target: { name, value: String(e.target.checked) } })
                }}
                disabled={disabled}
              />
            }
            label={<div className='displayInnerHTML'>{parseHtml(label)}</div>}
          />
        );
      case "radio":
        return (
          <FormControl component="fieldset" disabled={disabled}>
            <FormLabel component="legend">{<div className='displayInnerHTML'>{parseHtml(label)}</div>}</FormLabel>
            <RadioGroup
              row
              name={name}
              value={value}
              disabled={disabled}
              onChange={(e) => {
                if(disabled) return;
                onEditFunc(e)
              }}
            >
              {options.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={opt.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );
      case "rating":
        return (
          <FormControl component="fieldset" disabled={disabled}>
            <FormLabel component="legend">{<div className='displayInnerHTML'>{parseHtml(label)}</div>}</FormLabel>
            <Rating
              name={name}
              value={Number(value) || 0}
              disabled={disabled}
              onChange={(e, newVal) => {
                if(disabled) return;
                onEditFunc({ target: { name, value: String(newVal ?? "") } })
              }}
              {...parsedHints}
            />
          </FormControl>
        );
      case "hidden":
        return <Input type="hidden" name={name} value={value} />;
      case "category-break":
        return (
          <>
            <br />
            <FormLabel className="text-muted" {...parsedHints}>
              {<div className='displayInnerHTML'>{parseHtml(label)}</div>}
            </FormLabel>
          </>
        );
      default:
        return (
          <Input
            type={type}
            name={name}
            defaultValue={defaultValue}
            value={value}
            disabled={disabled}
            step={step}
            onChange={e => {if(disabled) return; onEditFunc(e)}}
            {...parsedHints}
          />
        );
    }
  };
    return (
    <FormGroup className="py-1">
        {InputExcludeLabels.includes(type) ? (
          <Row>
            <Col md="12" className={required ? " required-field" : ""}>
              {renderInput()}
            </Col>
          </Row>
        ) : InputNextLineLabels.includes(type) ? (
          <>
            <Row className="align-items-center">
              <Col md="12">
                <FormLabel component="legend" className={`mb-0${required ? " required-field" : ""}`}>
                    {<span className='displayInnerHTML'>{parseHtml(label)}</span>}
                </FormLabel>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col md="12">
                {renderInput()}
              </Col>
            </Row>
          </>
        ): (<Row className="align-items-center">
            <Col xs="12" md="3">
            <FormLabel component="legend" className={`mb-0${required ? " required-field" : ""}`}>
                {<span className='displayInnerHTML'>{parseHtml(label)}</span>}
            </FormLabel>
            </Col>
            <Col xs="12" md="9">
            {renderInput()}
            </Col>
        </Row>
        )}
    </FormGroup>
    );

};

// 1) Group your schema by “category‑break” entries
export const useGroupedSchema = (schema, title = "") => {
  return useMemo(() => {
    const groups = [];
    let currentGroup = null;
    if(!schema || schema.length === 0) {
      // If schema is empty, return a single group with the title
      return [{ id: -9999, title: title, items: [] }];
    }

    if(schema[0]?.question_type !== "category-break") {
      // If the first item is not a category-break, create a default group
      currentGroup = { id: -9999, title: title, items: [] };
      groups.push(currentGroup);
    }

    schema.forEach((field, index) => {
      if (field.question_type === "category-break") {
        // start a new category
        currentGroup = {
          id: field.id,
          title: field.question_text,
          items: []
        };
        groups.push(currentGroup);
      } else if (currentGroup) {
        // push into the current category
        currentGroup.items.push(field);
      } 
    });

    if(schema[schema.length -1]?.question_type === "category-break") {
      // If it's the last item, create a hidden input
      currentGroup.items.push({
        id: -9998,
        question_text: "[Hidden Input]",
        question_type: "hidden",
        defaultValue: undefined,
      });
    }

    return groups;
  }, [schema]);
};


export const generateTypeConfig = () => {
  const typeConfigMap = {};

  typeConfigMap.text = {
    label: "Text",
    renderOptionalFields: ({ formData, setFormData }) => (
      <FormGroup>
        <Label for="placeholder">Placeholder</Label>
        <Input
          id="placeholder"
          name="placeholder"
          value={formData.question_hint.placeholder || ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              question_hint: {
                ...formData.question_hint,
                placeholder: e.target.value,
              },
            })
          }
        />
      </FormGroup>
    ),
    validate: (data) => Boolean(data.question_hint.placeholder?.trim()),
    validationMessage: "Placeholder is required",
    renderDetail: ({ question_hint }) => (
      <p>
        <strong>Placeholder:</strong> {question_hint.placeholder}
      </p>
    ),
  };

  typeConfigMap.textarea = {
    label: "Textarea",
    renderOptionalFields: (props) =>
      typeConfigMap.text.renderOptionalFields(props),
    validate: (data) => Boolean(data.question_hint.placeholder?.trim()),
    validationMessage: "Placeholder is required",
    renderDetail: ({ question_hint }) => (
      <p>
        <strong>Placeholder:</strong> {question_hint.placeholder}
      </p>
    ),
  };

  typeConfigMap.number = {
    label: "Number",
    renderOptionalFields: ({ formData, setFormData }) => (
      <Row form>
        <Col md={6}>
          <FormGroup>
            <Label for="min">Min</Label>
            <Input
              type="number"
              id="min"
              name="min"
              value={formData.question_hint.min ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  question_hint: {
                    ...formData.question_hint,
                    min: Number(e.target.value),
                  },
                })
              }
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="max">Max</Label>
            <Input
              type="number"
              id="max"
              name="max"
              value={formData.question_hint.max ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  question_hint: {
                    ...formData.question_hint,
                    max: Number(e.target.value),
                  },
                })
              }
            />
          </FormGroup>
        </Col>
      </Row>
    ),
    validate: (data) =>
      Number(data.question_hint.min) < Number(data.question_hint.max),
    validationMessage: "Min should be less than Max",
    renderDetail: ({ question_hint }) => (
      <p>
        <strong>Min:</strong> {question_hint.min}, <strong>Max:</strong>{" "}
        {question_hint.max}
      </p>
    ),
  };

  typeConfigMap.rating = {
    label: "Rating",
    renderOptionalFields: ({ formData, setFormData }) => (
      <Row form>
        <Col md={6}>
          <FormGroup>
            <Label for="max">Max</Label>
            <Input
              type="number"
              id="max"
              name="max"
              value={formData.question_hint.max ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  question_hint: {
                    ...formData.question_hint,
                    max: Number(e.target.value),
                  },
                })
              }
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="precision">Precision</Label>
            <Input
              type="number"
              id="precision"
              name="precision"
              value={formData.question_hint.precision ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  question_hint: {
                    ...formData.question_hint,
                    precision: Number(e.target.value),
                  },
                })
              }
            />
          </FormGroup>
        </Col>
      </Row>
    ),
    validate: (data) =>
      Number(data.question_hint.max) > 0 &&
      Number(data.question_hint.precision) > 0,
    validationMessage: "Max and precision must be positive",
    renderDetail: ({ question_hint }) => (
      <p>
        <strong>Max:</strong> {question_hint.max}, <strong>Precision:</strong>{" "}
        {question_hint.precision}
      </p>
    ),
  };

  typeConfigMap.select = {
    label: "Select",
    renderOptionalFields: ({ formData, setFormData }) => (
      <FormGroup>
        <Label for="options">Options (JSON array)</Label>
        <Input
          type="textarea"
          id="options"
          name="options"
          rows="3"
          placeholder='eg. [
  {"value": "1", "label": "Option 1"}, 
  {"value": "2", "label": "Option 2"}
]'
          value={formData.question_hint.options || ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              question_hint: {
                ...formData.question_hint,
                options: e.target.value,
              },
            })
          }
        />
      </FormGroup>
    ),
    processData: (data) => {
      var options = [];
      try {
        options = JSON.parse(data.question_hint.options || "[]");
      } catch {
        options = [];
      } finally {
        return {
          ...data,
          question_hint: {
            ...data.question_hint,
            options: options.map((o) => ({
              value: o.value || o.label,
              label: o.label || o.value,
            })),
          },
        };
      }
    },
    validate: (data) => {
      try {
        const opts = JSON.parse(data.question_hint.options);
        return Array.isArray(opts) && opts.length > 0;
      } catch {
        return false;
      }
    },
    validationMessage: "Options must be a valid JSON array",
    renderDetail: ({ question_hint }) => {
      try {
        const opts = JSON.parse(question_hint.options);
        return (
          <ul>
            {opts.map((o, i) => (
              <li key={i}>{o.label || o.value}</li>
            ))}
          </ul>
        );
      } catch {
        return <p>No options provided</p>;
      }
    },
  };

  typeConfigMap["multi-select"] = {
    label: "Multi-Select",
    renderOptionalFields: (props) =>
      typeConfigMap.select.renderOptionalFields(props),
    validate: (data) => typeConfigMap.select.validate(data),
    processData: (data) => typeConfigMap.select.processData(data),
    validationMessage: typeConfigMap.select.validationMessage,
    renderDetail: (props) => typeConfigMap.select.renderDetail(props),
  };

  typeConfigMap.radio = {
    label: "Radio",
    renderOptionalFields: (props) =>
      typeConfigMap.select.renderOptionalFields(props),
    validate: (data) => typeConfigMap.select.validate(data),
    processData: (data) => typeConfigMap.select.processData(data),
    validationMessage: typeConfigMap.select.validationMessage,
    renderDetail: (props) => typeConfigMap.select.renderDetail(props),
  };

  typeConfigMap.checkbox = {
    label: "Checkbox",
    renderOptionalFields: () => null,
    validate: () => true,
    validationMessage: "",
    renderDetail: ({ question_hint }) => (
      <p>
        {question_hint.checked
          ? "Checked by default"
          : "Not checked by default"}
      </p>
    ),
  };
  return typeConfigMap;
};

export const surveyFieldSchemaTemplate = [
    {
      id: "00",
      question_text: "Care Concierge (Empty) Template",
      question_type: "category-break",
    },
    {
      id: "fullName",
      question_text: "Text Field (Empty) Template",
      question_type: "text",
      defaultValue: ""
    },
    {
      id: "email",
      question_text: "Email Address (Empty) Template",
      question_type: "email",
      defaultValue: ""
    },
    {
      id: "age",
      question_text: "Number Field (Empty) Template",
      question_type: "number",
      defaultValue: "18"
    },
    {
      id: "phone",
      question_text: "Contact Number (Empty) Template",
      question_type: "tel",
      defaultValue: ""
    },
    {
      id: "00",
      question_text: "Section Break (Empty) Template 1",
      question_type: "category-break",
    },
    {
      id: "hobbies_general",
      question_text: "How you hearth about us? (Template)",
      question_type: "radio",
      value: "[]",
      question_hint: JSON.stringify({
        options: [
          { value: "new_paper", label: "News Paper", score: -0.32393943 },
          { value: "social_media", label: "Social Media", score: -0.12345678 },
          { value: "word_of_mouth", label: "Word of Mouth", score: -0.23456789 },
          { value: "website", label: "Website", score: -0.34567890 },
          { value: "event", label: "Event", score: -0.45678901 },
          { value: "other", label: "Other", score: -0.56789012 }
        ]
      })
    },
    {
      id: "hobbies",
      question_text: "Endorse Caregiver Skills (Template)",
      question_type: "multi-select",
      value: "[]",
      question_hint: JSON.stringify({
        options: [
          { value: "hand_wash", label: "Hand Wash", score: 0.12345678 },
          { value: "oral_care", label: "Oral Care", score: 0.23456789 },
          { value: "bathing", label: "Bathing", score: 0.34567890 },
          { value: "dressing", label: "Dressing", score: 0.45678901 },
          { value: "feeding", label: "Feeding", score: 0.56789012 },
          { value: "mobility", label: "Mobility Assistance", score: 0.67890123 },
          { value: "toileting", label: "Toileting", score: 0.78901234 },
          { value: "transferring", label: "Transferring", score: 0.89012345 },
          { value: "wound_care", label: "Wound Care", score: 0.90123456 },
          { value: "vital_signs", label: "Vital Signs Monitoring", score: 1.01234567 },
          { value: "pain_management", label: "Pain Management", score: 1.12345678 },
          { value: "emotional_support", label: "Emotional Support", score: 1.23456789 },
          { value: "medication_management", label: "Medication Management", score: 1.34567890 }
        ]
      })
    },
    {
      id: "category",
      question_text: "Category Template",
      question_type: "select",
      value: "",
      question_hint: JSON.stringify({
        options: [
          { value: "general", label: "General", score: 0 },
          { value: "support", label: "Support", score: 0 },
          { value: "feedback", label: "Feedback", score: 0 }
        ]
      })
    },
    {
      id: "satisfaction",
      question_text: "Satisfaction About the Caregiver",
      question_type: "rating",
      value: "3",
      question_hint: JSON.stringify({ shape: "love", max: 10, precision: 1 })
    },
    {
      id: "satisfaction_2",
      question_text: "Caregiver's Professionalism and Attitude in the service hours",
      question_type: "rating",
      value: "3",
      question_hint: JSON.stringify({ shape: "love", max: 10, precision: 1 })
    },
    {
      id: "11",
      question_text: "Would you recommend her/him to others? (Optional)",
      question_type: "textarea",
      value: "",
      question_hint: "{placeholder: 'Please share any additional feedback or suggestions about the caregiver'}"
    },
    {
      id: "subscribe",
      question_text: "Subscribe for CARE Concierge Email Updates (Template)",
      question_type: "checkbox",
      value: "false"
    },
    {
      id: "0",
      question_text: "🌟 Recommendation (Net Promoter Score)",
      question_type: "category-break",
    },
    {
      id: "9",
      question_text: "How likely are you to recommend Komune Care to a friend or family member? ",
      question_type: "rating",
      value: "0",
      question_hint: {"max": 5,"precision": 1}
    },
    {
      id: "10",
      question_text: "Additional Comments (optional)",
      question_type: "textarea",
      value: "",
      question_hint: "{placeholder: 'Please share any additional feedback or suggestions'}"
    },
];

export const templateForm = {
    "title": "[Care Concierge Notice]",
    "centre": 6,
    "type": "centre",
    "in_used": "true",
    "heading": "<section class=\"cc-notice\" role=\"alert\" aria-live=\"polite\"> <h1>[Care Concierge Notice]</h1> <p>We regret to inform you that the survey you are trying to access is currently unavailable.</p> <p>This situation may arise for one of the following reasons:</p> <ul> <li>The survey link is no longer valid or may have been entered incorrectly.</li> <li>The survey has already reached its expiration date and is closed for responses.</li> </ul> <p> If you intended to provide feedback or share input, please know that your voice is still important to us. We encourage you to contact your Care Concierge administrator or support team, who will be happy to guide you to an updated survey or alternative feedback channel. </p> <p>Thank you for your understanding and for your continued engagement with Care Concierge.</p> </section>",
    "tailer": "<p><strong>Thank You for Your Engagement</strong></p><p>We sincerely appreciate your time, attention, and thoughtful contributions. Your engagement plays a vital role in ensuring the success and relevance of this initiative. Should you have any questions, feedback, or suggestions for improvement, we welcome your insights.</p><p>Let’s continue shaping meaningful experiences — together.</p>"
};

const to7 = (x) => Number(x.toFixed(7));
const num = (x) => (x === null || x === undefined || x === "" ? NaN : Number(x));
const parseHints = (h) => {
  try { return typeof h === "string" ? JSON.parse(h) : (h || {}); }
  catch { return {}; }
};

export function scoreAnswer(question, answer) {
  if (!question) {
    return { answer_value: String(answer ?? ""), normalize_score: null };
  }

  const type = question.question_type;
  const hints = parseHints(question.question_hint);

  switch (type) {
    case "number": {
      const v = num(answer);
      const min = num(hints.min);
      const max = num(hints.max);
      if (Number.isFinite(v) && Number.isFinite(min) && Number.isFinite(max) && max > min) {
        const mid = (max + min) / 2;
        const halfRange = (max - min) / 2;
        return { answer_value: String(answer ?? ""), normalize_score: to7((v - mid) / halfRange) };
      }
      return { answer_value: String(answer ?? ""), normalize_score: null };
    }

    case "rating": {
      const v = num(answer);
      const max = Number.isFinite(num(hints.max)) ? num(hints.max) : 5;
      if (Number.isFinite(v) && max > 0) {
        const mid = max / 2;
        return { answer_value: String(answer ?? ""), normalize_score: to7((v - mid) / mid) };
      }
      return { answer_value: String(answer ?? ""), normalize_score: null };
    }

    case "checkbox": {
      const checked = (String(answer).toLowerCase() === "true");
      return { answer_value: String(checked), normalize_score: checked ? 1.0000000 : -1.0000000 };
    }

    case "radio":
    case "select": {
      const options = Array.isArray(hints.options) ? hints.options : [];
      // answer is a single value (string)
      const picked = options.find(o => String(o.value) === String(answer));
      if (!picked || typeof picked.score !== "number") {
        return { answer_value: String(answer ?? ""), normalize_score: null };
      }
      return { answer_value: String(answer ?? ""), normalize_score: to7(picked.score) };
    }

    case "multi-select": {
      const options = Array.isArray(hints.options) ? hints.options : [];
      let arr;
      try { arr = JSON.parse(answer || "[]"); } catch { arr = []; }
      const pickedScores = arr
        .map(v => options.find(o => String(o.value) === String(v))?.score)
        .filter((s) => typeof s === "number");

      if (pickedScores.length === 0) {
        return { answer_value: JSON.stringify(arr), normalize_score: null };
      }
      const avg = pickedScores.reduce((a,b)=>a+b,0) / pickedScores.length;
      return { answer_value: JSON.stringify(arr), normalize_score: to7(avg) };
    }

    case "text":
    case "textarea":
      // can do embedding evaluation later if need
    case "category-break":
    case "form-break":
    case "hidden":
    case "email":
    case "tel":
      return { answer_value: String(answer ?? ""), normalize_score: null };

    default:
      return { answer_value: String(answer ?? ""), normalize_score: null };
  }
}

export function buildSurveyPayload(src, schema) {
  // quick index by question_id
  const qIndex = Object.fromEntries(schema.map(q => [String(q.question_id), q]));

  return Object.entries(src)
    .filter(([id]) => id !== "undefined" && !isNaN(Number(id)) && Number(id) > 0)
    .map(([id, value]) => {
      const q = qIndex[String(id)];
      const { answer_value, normalize_score } = scoreAnswer(q, value);
      return {
        survey_question_id: Number(id),
        answer_value,
        normalize_score
      };
    });
}

export function searchForOverallRating(src, schema) {
  // quick index by question_id
  const overallRatingQuestion = schema.find(q => q.question_type === "rating" && (q.name || "").toLowerCase() === "overall");
  const value = src[String(overallRatingQuestion?.question_id)] ?? null;
  if(overallRatingQuestion?.answer_value !== undefined) return scoreAnswer(overallRatingQuestion, overallRatingQuestion?.answer_value);
  if(!value) return null;
  const { answer_value, normalize_score } = scoreAnswer(overallRatingQuestion, value);
  return { answer_value, normalize_score };
}